import { Storage } from '../lib/storage';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, AlertCircle, PhoneCall, AlertOctagon } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function SOSScreen({ onOpenConfig }: { onOpenConfig?: () => void }) {
  const [isActive, setIsActive] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(5);
  const [status, setStatus] = useState<'idle' | 'warning' | 'fired'>('idle');

  useEffect(() => {
    // Restaurar estado visual si existe una alerta en servidor al abrir la pantalla
    const checkActiveSOS = async () => {
      const myId = Storage.get('gravity_user_id');
      if (!myId) return;
      const { data } = await supabase.from('alertas_sos').select('id').eq('usuario_id', myId).eq('estado', 'ACTIVA').limit(1);
      if (data && data.length > 0) {
         setStatus('fired');
         setIsActive(true);
         setSecondsLeft(0);
      }
    };
    checkActiveSOS();
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let autoOffTimer: ReturnType<typeof setTimeout>;

    if (isActive && secondsLeft > 0) {
      timer = setInterval(() => {
        setSecondsLeft(prev => prev - 1);
      }, 1000);
    } else if (isActive && secondsLeft === 0 && status !== 'fired') {
      setStatus('fired');
      enviarAlertaBackend();
    }
    
    // Auto-Apagado Boma de Tiempo (Muerte Súbita 2 Horas = 7,200,000 ms)
    if (status === 'fired') {
      autoOffTimer = setTimeout(() => {
        cancelPanic();
        alert('Tu alarma S.O.S ha sido desactivada automáticamente al cumplirse el tiempo límite de 2 horas.');
      }, 7200000);
    }

    return () => {
      clearInterval(timer);
      if (autoOffTimer) clearTimeout(autoOffTimer);
    };
  }, [isActive, secondsLeft, status]);

  const enviarAlertaBackend = async () => {
    try {
      // 1. Obtener coordenadas reales
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          dispararAlerta(lat, lng);
        }, (error) => {
          console.error("GPS Error:", error);
          dispararAlerta(0, 0); // Disparar sin ubicacion
        }, { enableHighAccuracy: true });
      } else {
        dispararAlerta(0, 0);
      }
    } catch(e) {
      console.error("Error contactando servidor de emergencia", e);
    }
  };

  const dispararAlerta = async (lat: number, lng: number) => {
     try {
         const myId = Storage.get('gravity_user_id');
         let miNombreReal = 'Un Piloto';
         let miPropioCelular = '';

         if (myId) {
             const { data: ud } = await supabase.from('usuarios').select('nombre, celular, es_oro').eq('id', myId).single();
             if (ud) {
                // Formatear a Title Case para que se vea elegante
                miNombreReal = ud.nombre ? ud.nombre.toLowerCase().split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Un Piloto';
                miPropioCelular = ud.celular || '';
             }

             const motero1 = Storage.get('gravity_sos_motero1Celular');
             const fam1 = Storage.get('gravity_sos_fam1Celular');
             
             let destinos = [];
             const cleanM1 = motero1 ? motero1.replace(/\D/g, '') : '';
             const cleanF1 = fam1 ? fam1.replace(/\D/g, '') : '';

             if (cleanM1.length >= 7) destinos.push(cleanM1);
             if (cleanF1.length >= 7) destinos.push(cleanF1);
             
             if (ud?.es_oro) {
                 const motero2 = Storage.get('gravity_sos_motero2Celular');
                 const motero3 = Storage.get('gravity_sos_motero3Celular');
                 
                 const cleanM2 = motero2 ? motero2.replace(/\D/g, '') : '';
                 const cleanM3 = motero3 ? motero3.replace(/\D/g, '') : '';
                 
                 if (cleanM2.length >= 7) destinos.push(cleanM2);
                 if (cleanM3.length >= 7) destinos.push(cleanM3);
             }
             const userObj = JSON.parse(Storage.get('gravity_user') || '{}');
             const userPin = userObj.pin || '';
             let errDB: any = null;

             // 1. Intentar llamada RPC segura primero
             const { data: rpcResult, error: rpcErr } = await supabase.rpc('crear_sos_seguro', {
                 p_usuario_id: myId,
                 p_pin_ingresado: userPin,
                 p_latitud: lat,
                 p_longitud: lng,
                 p_celulares_rescate: destinos
             });

             if (rpcErr) {
                 console.warn("La RPC segura crear_sos_seguro falló, intentando fallback directo:", rpcErr);
                 // Fallback directo a insert (si RLS lo permite)
                 const { error: directErr } = await supabase.from('alertas_sos').insert([{
                     usuario_id: myId,
                     latitud: lat,
                     longitud: lng,
                     estado: 'ACTIVA',
                     celulares_rescate: destinos
                 }]);
                 errDB = directErr;
             } else if (rpcResult && rpcResult.success === false) {
                 errDB = { message: rpcResult.error || 'PIN de seguridad inválido' };
             }

             if (errDB) {
                 alert(`Atención Gravity Bike: El S.O.S falló al guardar en Supabase. Error: ${errDB.message}. Toma captura y mándasela al ingeniero.`);
             }
             
             // Trick: Esperar 800ms para evitar que Android/HyperOS aborte la conexión de red por abrir WhatsApp muy rápido
             await new Promise(resolve => setTimeout(resolve, 800));
         }
         
         // 2. Disparar WhatsApp al Contacto 1 configurado
         const famCelularRaw = Storage.get('gravity_sos_fam1Celular');
         if (famCelularRaw) {
             let celularNumero = famCelularRaw.replace(/\D/g, ''); // Solo numeros
             // Autoprefijo inteligente para Colombia
             if (celularNumero.length === 10) {
                 celularNumero = '57' + celularNumero;
             }
             const urlGPS = lat !== 0 ? `https://maps.google.com/?q=${lat},${lng}` : 'Ubicación GPS no disponible.';
             
             const texto = `*¡ALERTA S.O.S GRAVITY BIKE!*\n\nSoy *(${miNombreReal})*. Acabo de presionar mi Botón de Pánico. Podría estar en una situación de riesgo o accidente.\n\n*Ubicación del reporte:*\n${urlGPS}\n\n*Por favor intenta comunicarte conmigo ahora mismo al Celular (${miPropioCelular}).*\n\n*Mensaje automático autogenerado por Gravity Bike.*\n\nhttps://pikishub.com/gravitybike/`;
             
             // Trick asíncrono: crear un botón fantasma y presionarlo para evadir el bloqueo de Android
             const link = document.createElement('a');
             link.href = `whatsapp://send?phone=${celularNumero}&text=${encodeURIComponent(texto)}`;
             link.target = '_top';
             document.body.appendChild(link);
             link.click();
             setTimeout(() => document.body.removeChild(link), 300);
         }
     } catch (err) {
         console.error(err);
     }
  };

  const triggerPanic = () => {
    if (status === 'fired') return;
    setIsActive(true);
    setStatus('warning');
    setSecondsLeft(5);
  };

  const cancelPanic = async () => {
    setIsActive(false);
    setStatus('idle');
    setSecondsLeft(5);

    try {
        const userId = Storage.get('gravity_user_id');
        const userObj = JSON.parse(Storage.get('gravity_user') || '{}');
        const userPin = userObj.pin || '';

        if (userId) {
            // Intentar llamada RPC segura primero
            const { data: rpcSuccess, error: rpcErr } = await supabase.rpc('resolver_sos_seguro', {
                p_usuario_id: userId,
                p_pin_ingresado: userPin
            });

            if (rpcErr || !rpcSuccess) {
                console.warn("Fallo RPC resolver_sos_seguro, intentando fallback directo:", rpcErr);
                // Fallback directo
                await supabase.from('alertas_sos')
                    .update({ estado: 'RESUELTA' })
                    .eq('usuario_id', userId)
                    .eq('estado', 'ACTIVA');
            }
        }
    } catch(e) { console.error(e) }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center h-full text-center p-4"
    >
      <div className="flex-1 w-full flex flex-col items-center justify-center">
        <button 
          onClick={triggerPanic}
          className={`w-48 h-48 rounded-full flex items-center justify-center text-4xl font-extrabold transition-all duration-300 mb-10 border-[8px] outline-none select-none shadow-[0_0_50px_rgba(239,68,68,0.6)]
            ${status === 'idle' ? 'bg-gradient-to-b from-red-500 to-red-600 text-white border-red-950 active:scale-95' : ''}
            ${status === 'warning' ? 'bg-white text-red-600 border-red-600 animate-pulse shadow-[0_0_80px_rgba(239,68,68,0.9)]' : ''}
            ${status === 'fired' ? 'bg-green-500 text-white border-green-950 shadow-[0_0_60px_rgba(34,197,94,0.6)]' : ''}
          `}
        >
          {status === 'idle' ? 'SOS' : status === 'warning' ? secondsLeft : 'ENVIADA'}
        </button>

        <div className={`mb-5 font-bold text-xl ${status === 'warning' ? 'text-danger-red' : status === 'fired' ? 'text-green-400' : 'text-text-main'}`}>
          {status === 'idle' && '¿ESTÁS EN RIESGO?'}
          {status === 'warning' && '¡ALERTA ENVIÁNDOSE!'}
          {status === 'fired' && '¡ALERTA REPORTADA!'}
        </div>

        <p className="text-text-muted text-sm mb-8 px-4">
          {status === 'idle' && 'Toca el botón para alertar a los GRAVITY BIKE amigos.'}
          {status === 'warning' && `Enviando ubicación y SOS en ${secondsLeft}s`}
          {status === 'fired' && 'Se ha notificado a 5 GRAVITY BIKE amigos en un radio de 5km y a tus contactos.'}
        </p>

        {status === 'idle' && (
          <button 
            onClick={onOpenConfig}
            className="bg-white/5 border border-white/10 px-6 py-3 rounded-full flex items-center gap-2 hover:bg-white/10 transition-colors shadow-sm mb-6"
          >
            <PhoneCall className="w-4 h-4 text-white/70" />
            <span className="text-xs font-bold text-white/80 uppercase tracking-widest">Configurar Rescatistas</span>
          </button>
        )}

        {status !== 'idle' && (
          <button 
            onClick={cancelPanic}
            className="bg-transparent border border-text-muted px-8 py-3 rounded-full text-sm font-bold hover:bg-white/10 transition-colors"
          >
            {status === 'warning' ? 'Falsa Alarma (Cancelar)' : 'Estoy a Salvo (Apagar)'}
          </button>
        )}
      </div>

      {/* Marca de agua institucional GRAVITY BIKE */}
      <div className="mt-auto mb-4 pt-4 text-[10px] text-text-muted uppercase tracking-widest font-bold">
        Ecosistema Oficial{' '}
        <span className="text-white">GRAVITY BIKE</span>
      </div>
    </motion.div>
  );
}
